/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * A single call: what happened, the recording, and the transcript.
 *
 * The player is a plain <audio controls>. A custom waveform would look better
 * and do nothing an agent needs; the browser's own controls already handle
 * seeking, speed and keyboard access. The signed URL is fetched on demand and
 * expires, so it is never embedded in the page's initial payload.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, CircularProgress, Grid, IconButton } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MonoText from "components/MonoText";
import StatusBadge from "components/StatusBadge";
import InfoCard from "components/detail/InfoCard";
import DetailField from "components/detail/DetailField";
import ConversationRecording from "components/CallRecording/ConversationRecording";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { formatDuration } from "telephony/callStates";
import { getApiErrorMessage } from "utils/apiErrors";

export default function CallDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();

  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/telephony/calls/${id}`);
      setCall(res.data);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not load this call."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  // A failed load used to render the error above a full skeleton of empty
  // cards, which reads as "this call has no data" rather than "this call could
  // not be fetched". There is nothing to lay out around, so do not lay out.
  if (!call) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={4} pb={3} px={2}>
          <MDBox display="flex" alignItems="center" gap={1} mb={3}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
            <MDTypography variant="h5" fontWeight="medium">
              Call
            </MDTypography>
          </MDBox>
          <Alert severity={error ? "error" : "info"}>
            {error || "This call could not be found. It may have been deleted."}
          </Alert>
        </MDBox>
      </DashboardLayout>
    );
  }

  const counterparty = call?.direction === "outbound" ? call?.to_address : call?.from_address;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3} px={2}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <MDBox display="flex" alignItems="center" gap={1} mb={3}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <MDBox>
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <MonoText variant="h6">{counterparty || "Unknown number"}</MonoText>
              {call?.status && <StatusBadge status={call.status} label={call.status} size="sm" />}
            </MDBox>
            <MDTypography variant="body2" color="text">
              {call?.direction === "outbound" ? "Outbound call" : "Inbound call"}
              {call?.assigned_to_name ? ` · ${call.assigned_to_name}` : ""}
            </MDTypography>
          </MDBox>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <MDBox display="flex" flexDirection="column" gap={3}>
              <InfoCard icon="call" title="Call">
                <Grid container spacing={2}>
                  <DetailField label="Direction" value={call?.direction} />
                  <DetailField label="Status" value={call?.status} />
                  <DetailField label="Outcome" value={call?.disposition_name} />
                  <DetailField
                    label="Waited"
                    value={call?.wait_seconds != null ? formatDuration(call.wait_seconds) : null}
                  />
                  <DetailField
                    label="Talk time"
                    value={call?.talk_seconds != null ? formatDuration(call.talk_seconds) : null}
                  />
                  <DetailField
                    label="Started"
                    value={call?.started_at ? new Date(call.started_at).toLocaleString() : null}
                  />
                </Grid>
                {call?.wrap_up_notes && (
                  <MDBox mt={2}>
                    <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                      Notes
                    </MDTypography>
                    <MDTypography variant="button" color="dark">
                      {call.wrap_up_notes}
                    </MDTypography>
                  </MDBox>
                )}
              </InfoCard>

              <InfoCard icon="graphic_eq" title="Conversation & Recording">
                <ConversationRecording conversationId={id} autoLoadRecording />
              </InfoCard>
            </MDBox>
          </Grid>

          <Grid item xs={12} lg={4}>
            <InfoCard icon="link" title="Linked record">
              <Grid container spacing={2}>
                <DetailField label="Account" value={call?.account_name} md={12} />
                <DetailField label="Contact" value={call?.contact_name} md={12} />
                <DetailField label="Agent" value={call?.assigned_to_name} md={12} />
              </Grid>
              {call?.primary_parent_table && call?.primary_parent_id && (
                <MDBox mt={2}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() =>
                      window.open(
                        `/${call.primary_parent_table}/${call.primary_parent_id}`,
                        "_blank"
                      )
                    }
                  >
                    Open record
                  </MDButton>
                </MDBox>
              )}
            </InfoCard>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
